import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import { useQuery } from 'react-apollo';

import { Layout } from '../../../components/Layout';
import { FeedbackBar } from '../../../components/FeedbackBar';
import { Button } from '../../../components/shared/Button';
import ArrowIcon from '../../../images/icon-arrow';
import useLocationStorage from './useLocationStorage';
import { CLASSIC_HEADER_TYPE, MENU_TYPE, VERTICAL_HEADER_TYPE } from './enums';
import { SessionContext } from '../../../contexts/session';
import { defaultMenuColors, useMenuColorsStorage } from './useColorsStorage';
import { groupTaps } from './utils';
import { GET_TAPS_BY_STATUS } from '../queries';
import { getFilteredTapList } from '../../../util/tap';
import { buildPrintSettingsFromState, buildPrintUrl } from './serializeSettings';
import { checkFileSize, MAX_SCREEN_IMAGE_SIZE_BYTES } from '../../../util/files';

import { TemplateSection } from './sections/TemplateSection';
import { ContentSection } from './sections/ContentSection';
import { DisplayOptionsSection } from './sections/DisplayOptionsSection';
import { StyleSection } from './sections/StyleSection';

export const PrintSetup = () => {
  const { premium } = useContext(SessionContext);

  if (!premium) {
    return <Redirect to="/taps" />;
  }

  return <PrintSetupContent />;
};

const MENU_TYPE_LABELS: Record<string, string> = {
  [MENU_TYPE.DEFAULT]: 'Default',
  [MENU_TYPE.CLASSIC]: 'Classic',
  [MENU_TYPE.VERTICAL]: 'Vertical',
  [MENU_TYPE.NEW_YORK]: 'New York'
};

const GROUP_BY_LABELS: Record<string, string> = {
  none: '',
  style: 'Style',
  category: 'Category',
  brewery: 'Brewery'
};

const PrintSetupContent = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentLocation } = useContext(SessionContext);

  const [menuType, setMenuType] = useLocationStorage('menuType', MENU_TYPE.CLASSIC);
  const [groupBy, setGroupBy] = useLocationStorage('printGroupBy', 'none');
  const [title, setTitle] = useLocationStorage('printTitle', `${currentLocation?.name} Tap List`);
  const [showDate, setShowDate] = useLocationStorage('printShowDate', false);
  const [showDescription, setShowDescription] = useLocationStorage('printShowDescription', false);
  const [columns, setColumns] = useLocationStorage('printColumns', 2);
  const [showHandle, setShowHandle] = useLocationStorage('printShowHandle', true);
  const [showBreweryName, setShowBreweryName] = useLocationStorage('printShowBreweryName', true);
  const [showServings, setShowServings] = useLocationStorage('printShowServings', true);
  const [showServingSize, setShowServingSize] = useLocationStorage('printShowServingSize', false);
  const [showServingCurrency, setShowServingCurrency] = useLocationStorage('printShowServingCurrency', false);
  const [showBeerStyle, setShowBeerStyle] = useLocationStorage('printShowBeerStyle', false);
  const [showLocation, setShowLocation] = useLocationStorage('printShowLocation', false);
  const [showLogo, setShowLogo] = useLocationStorage('printShowLogo', false);
  const [sortBy, setSortBy] = useLocationStorage('printSortBy', 'handle');
  const [tapStart, setTapStart] = useLocationStorage('printTapStart', 1);
  const [tapEnd, setTapEnd] = useLocationStorage('printTapEnd', 0);
  const menuColors = defaultMenuColors[menuType as MENU_TYPE] ?? {};
  const [primaryColor, setPrimaryColor] = useMenuColorsStorage('primaryColor', menuType, (menuColors as any).primary);
  const [secondaryColor, setSecondaryColor] = useMenuColorsStorage('secondaryColor', menuType, (menuColors as any).secondary);
  const [primaryTextColor, setPrimaryTextColor] = useMenuColorsStorage('primaryTextColor', menuType, (menuColors as any).primaryText);
  const [secondaryTextColor, setSecondaryTextColor] = useMenuColorsStorage(
    'secondaryTextColor',
    menuType,
    (menuColors as any).secondaryText
  );
  const [classicHeaderType, setClassicHeaderType] = useLocationStorage('classicHeaderType', CLASSIC_HEADER_TYPE.LEFT_ALIGN);
  const [verticalHeaderType, setVerticalHeaderType] = useLocationStorage('verticalHeaderType', VERTICAL_HEADER_TYPE.PRIMARY);
  const [dashColor, setDashColor] = useMenuColorsStorage('dashColor', menuType, (menuColors as any).dash);
  const [footerDate, setFooterDate] = useLocationStorage('footerDate', '');
  const [footerLocation, setFooterLocation] = useLocationStorage('footerLocation', '');
  const [footerWebsite, setFooterWebsite] = useLocationStorage('footerWebsite', '');

  const [headerImageUrl, setHeaderImageUrl] = useState<string>();
  const [headerImagePreviewUrl, setHeaderImagePreviewUrl] = useState<string>();
  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [footerImageUrl, setFooterImageUrl] = useState<string>();
  const [footerImagePreviewUrl, setFooterImagePreviewUrl] = useState<string>();
  const [footerImageFile, setFooterImageFile] = useState<File | null>(null);

  const isGrouped = groupBy !== 'none';
  const [groupOrder, setGroupOrder] = useState<Array<{ id: string; groupName: string; taps: any[]; index: number }>>([]);

  const [error, setError] = useState<string | null>(null);

  const isCustomMenu = menuType === MENU_TYPE.CLASSIC || menuType === MENU_TYPE.VERTICAL;
  const isClassicMenu = menuType === MENU_TYPE.CLASSIC;
  const isVerticalMenu = menuType === MENU_TYPE.VERTICAL;
  const isNewYorkMenu = menuType === MENU_TYPE.NEW_YORK;
  const allowedColumnOptions = isNewYorkMenu ? [2] : [1, 2, 3];
  const effectiveColumns = isNewYorkMenu ? 2 : columns;
  const headerTypeIsLogo =
    (isClassicMenu && classicHeaderType === CLASSIC_HEADER_TYPE.LOGO) ||
    (isVerticalMenu && verticalHeaderType === VERTICAL_HEADER_TYPE.LOGO);

  const { data: tapsData } = useQuery(GET_TAPS_BY_STATUS, {
    variables: { location: currentLocation?._id, statuses: ['ACTIVE'] },
    fetchPolicy: 'cache-and-network'
  });

  const tapsCount = tapsData?.taps?.length ?? 0;

  useEffect(() => {
    if (tapEnd === 0 && tapsCount > 0) {
      setTapEnd(tapsCount);
    }
  }, [setTapEnd, tapEnd, tapsCount]);

  useEffect(() => {
    if (isNewYorkMenu && columns !== 2) {
      setColumns(2);
    }
  }, [isNewYorkMenu, columns, setColumns]);

  const resetGroupOrder = () => {
    if (!tapsData) return;
    const tapList = getFilteredTapList(tapStart, tapEnd, tapsData.taps);

    if (isGrouped) {
      const defaultGroup = t('PrintedMenu.Other');
      const groups = Object.entries(groupTaps(tapList, groupBy as any, defaultGroup)).map(([groupName, taps], index) => ({
        id: (taps as any[])[0]?._id ?? `group-${index}`,
        groupName,
        taps: taps as any[],
        index
      }));
      setGroupOrder(groups);
    } else {
      setGroupOrder([]);
    }
  };

  useEffect(() => {
    resetGroupOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tapsData, groupBy, tapStart, tapEnd]);

  const handleHeaderImageSelection = (file: File, previewUrl: string) => {
    setError('');
    const fileSizeError = checkFileSize(file, MAX_SCREEN_IMAGE_SIZE_BYTES);
    if (fileSizeError) {
      setError(fileSizeError.message);
    } else {
      const blob = new Blob([file], { type: file.type });
      const blobURL = URL.createObjectURL(blob);
      setHeaderImageUrl(blobURL);
      setHeaderImagePreviewUrl(previewUrl);
      setHeaderImageFile(file);
    }
  };

  const removeHeaderImage = () => {
    setHeaderImagePreviewUrl(undefined);
    setHeaderImageUrl(undefined);
    setHeaderImageFile(null);
  };

  const handleFooterImageSelection = (file: File, previewUrl: string) => {
    setError('');
    const fileSizeError = checkFileSize(file, MAX_SCREEN_IMAGE_SIZE_BYTES);
    if (fileSizeError) {
      setError(fileSizeError.message);
    } else {
      const blob = new Blob([file], { type: file.type });
      const blobURL = URL.createObjectURL(blob);
      setFooterImageUrl(blobURL);
      setFooterImagePreviewUrl(previewUrl);
      setFooterImageFile(file);
    }
  };

  const removeFooterImage = () => {
    setFooterImagePreviewUrl(undefined);
    setFooterImageUrl(undefined);
    setFooterImageFile(null);
  };

  const dragEnded = (param: any) => {
    const { source, destination } = param;
    if (!destination) return;
    const arr = [...groupOrder];
    const item = arr.splice(source.index, 1)[0];
    arr.splice(destination.index, 0, item);
    setGroupOrder(arr);
  };

  const handlePrint = () => {
    const settings = buildPrintSettingsFromState({
      menuType,
      groupBy,
      title,
      showDate,
      showDescription,
      showLocation,
      showLogo,
      columns: effectiveColumns,
      showHandle,
      sortBy,
      showBreweryName,
      showServings,
      showServingSize,
      showServingCurrency,
      showBeerStyle,
      tapStart,
      tapEnd,
      primaryColor,
      secondaryColor,
      dashColor,
      primaryTextColor,
      secondaryTextColor,
      footerDate,
      footerLocation,
      footerWebsite,
      groupOrder,
      isCustomMenu,
      isClassicMenu,
      isVerticalMenu,
      classicHeaderType,
      verticalHeaderType,
      headerImageFile,
      headerImageUrl,
      footerImageFile,
      footerImageUrl
    });

    window.open(buildPrintUrl(settings));
  };

  const summaryParts = [
    MENU_TYPE_LABELS[menuType] ?? menuType,
    t('PrintDialog.SummaryColumns', { count: effectiveColumns }),
    isGrouped ? t('PrintDialog.SummaryGroupedBy', { group: GROUP_BY_LABELS[groupBy] || groupBy }) : null,
    t('PrintDialog.SummaryTapRange', { start: tapStart, end: tapEnd })
  ].filter(Boolean);

  return (
    <Layout title={t('PrintDialog.PrintTapMenu')} className="!pt-0 font-public-sans">
      <div className="flex flex-col">
        <div className="px-4 pt-1.5 pb-3 border-b border-accent-grey">
          <button onClick={() => history.push('/taps')} className="p-2 hover:bg-light-grey rounded-full">
            <ArrowIcon className="w-6 h-6" />
          </button>
        </div>

        <FeedbackBar message={error} />

        <div className="px-4 py-3">
          <p className="text-sm text-gray-500">{summaryParts.join(' \u2022 ')}</p>
        </div>

        <div className="px-4 pb-32 space-y-6">
          <TemplateSection menuType={menuType} onMenuTypeChange={setMenuType} />

          <ContentSection
            title={title}
            onTitleChange={setTitle}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            columns={columns}
            onColumnsChange={setColumns}
            columnOptions={allowedColumnOptions}
            tapStart={tapStart}
            onTapStartChange={setTapStart}
            tapEnd={tapEnd}
            onTapEndChange={setTapEnd}
            isGrouped={isGrouped}
            groupOrder={groupOrder}
            onDragEnd={dragEnded}
          />

          <DisplayOptionsSection
            showHandle={showHandle}
            onShowHandleChange={setShowHandle}
            showBreweryName={showBreweryName}
            onShowBreweryNameChange={setShowBreweryName}
            showBeerStyle={showBeerStyle}
            onShowBeerStyleChange={setShowBeerStyle}
            showLocation={showLocation}
            onShowLocationChange={setShowLocation}
            showDescription={showDescription}
            onShowDescriptionChange={setShowDescription}
            showServings={showServings}
            onShowServingsChange={setShowServings}
            showServingSize={showServingSize}
            onShowServingSizeChange={setShowServingSize}
            showServingCurrency={showServingCurrency}
            onShowServingCurrencyChange={setShowServingCurrency}
            showDate={showDate}
            onShowDateChange={setShowDate}
            showLogo={showLogo}
            onShowLogoChange={setShowLogo}
            isCustomMenu={isCustomMenu}
          />

          {isCustomMenu && (
            <StyleSection
              isClassicMenu={isClassicMenu}
              isVerticalMenu={isVerticalMenu}
              primaryColor={primaryColor}
              onPrimaryColorChange={(hex) => setPrimaryColor(hex)}
              secondaryColor={secondaryColor}
              onSecondaryColorChange={(hex) => setSecondaryColor(hex)}
              primaryTextColor={primaryTextColor}
              onPrimaryTextColorChange={(hex) => setPrimaryTextColor(hex)}
              secondaryTextColor={secondaryTextColor}
              onSecondaryTextColorChange={(hex) => setSecondaryTextColor(hex)}
              dashColor={dashColor}
              onDashColorChange={(hex) => setDashColor(hex)}
              classicHeaderType={classicHeaderType}
              onClassicHeaderTypeChange={setClassicHeaderType}
              verticalHeaderType={verticalHeaderType}
              onVerticalHeaderTypeChange={setVerticalHeaderType}
              headerTypeIsLogo={headerTypeIsLogo}
              headerImagePreviewUrl={headerImagePreviewUrl}
              headerImageUrl={headerImageUrl}
              onHeaderImageSelected={handleHeaderImageSelection}
              onRemoveHeaderImage={removeHeaderImage}
              footerImagePreviewUrl={footerImagePreviewUrl}
              footerImageUrl={footerImageUrl}
              onFooterImageSelected={handleFooterImageSelection}
              onRemoveFooterImage={removeFooterImage}
              footerDate={footerDate}
              onFooterDateChange={setFooterDate}
              footerLocation={footerLocation}
              onFooterLocationChange={setFooterLocation}
              footerWebsite={footerWebsite}
              onFooterWebsiteChange={setFooterWebsite}
            />
          )}
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-accent-grey p-4 md:relative md:border-0">
          <div className="flex justify-end space-x-2 max-w-[1000px] mx-auto">
            <Button variant="secondary" onClick={() => history.push('/taps')}>
              {t('PrintDialog.Cancel')}
            </Button>
            <Button variant="primary" onClick={handlePrint}>
              {t('PrintDialog.Print')}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};
